import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "../../assets/images/Helix Conference.webp";

const Header = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const nav_links = [
    { name: "Home", link: "/" },
    { name: "About Us", link: "/about" },
    { name: "Conferences", link: "/Conferences" },
    { name: "Journals", link: "/journals" },
    { name: "Gallery", link: "/gallery" },
    { name: "Testimonials", link: "/testimonials" },
    { name: "Call For Papers", link: "/call-for-papers" },
    { name: "Contact", link: "/contact" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      requestAnimationFrame(() => {
        setScrolled(window.scrollY > 80);
      });
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  return (
    <header className="bg-white fixed top-0 left-0 z-50 w-full shadow-sm">
      <div className="flex items-center justify-between md:px-6 px-4 py-2 transition-all duration-300">
        
        {/* Logo - Made attractive and responsive for all screen sizes */}
        <div className="flex-shrink-0 z-50 w-32 sm:w-40 md:w-48 lg:w-[200px]">
          <Link to="/">
            <img
              src={Logo}
              alt="Helix Conferences"
              className="h-14 sm:h-16 md:h-20 lg:h-[80px] w-auto ml-4 object-contain transform scale-[1.5] lg:scale-[1.8] origin-left transition-all duration-300"
              style={{
                // Responsive heights that shrink smoothly on scroll
                maxHeight: scrolled ? "3.5rem" : "5rem",
              }}
            />
          </Link>
        </div>

        <nav
          className={`hidden xl:flex items-center text-[14px] xl:text-[15px] font-semibold ${
            scrolled ? "gap-4 xl:gap-6 ml-2" : "gap-4 xl:gap-6 ml-2 xl:ml-4"
          }`}
        >
          {nav_links.map((item, index) => {
            const isActive = location.pathname === item.link || (item.link !== "/" && location.pathname.startsWith(item.link));
            return (
              <Link
                key={index}
                to={item.link}
                className={`whitespace-nowrap transition-all duration-200 pb-1 relative group ${
                  isActive
                    ? "text-[#546DC5]"
                    : "text-gray-700 hover:text-[#546DC5]"
                }`}
                style={{ marginRight: scrolled ? "0px" : "4px" }}
              >
                {item.name}
                {/* Modern Hover/Active Underline Animation */}
                <span 
                  className={`absolute left-0 bottom-0 w-full h-[2px] bg-[#546DC5] transition-transform duration-300 origin-left
                    ${isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}
                  `}
                ></span>
              </Link>
            );
          })}
        </nav>

        <div className="hidden xl:flex items-center gap-3 xl:gap-5 ml-4 lg:ml-8">
          <div className="flex items-center gap-3">
            <Link
              to="/buy-a-ticket"
              className="whitespace-nowrap py-2.5 px-6 text-white font-bold rounded-full bg-[#546DC5] hover:bg-[#4357a3] shadow-md hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5"
            >
              Buy A Ticket
            </Link>
            {/* Keeping the translate element space exactly like subdomains */}
            <div className="flex flex-col items-start justify-center min-w-[60px] 2xl:min-w-[120px]">
              <div id="google_translate_element" className="font-sm text-[#546DC5]"></div>
            </div>
          </div>
        </div>

        <div className="xl:hidden ml-auto z-50">
          <button 
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {isOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </div>

      {/* Mobile/Tablet Navigation Menu */}
      {isOpen && (
        <div className="xl:hidden absolute top-full left-0 w-full bg-white px-4 pb-6 pt-2 shadow-xl border-t border-gray-100">
          <div className="flex flex-col space-y-2">
            {nav_links.map((item, index) => {
              const isActive = location.pathname === item.link || (item.link !== "/" && location.pathname.startsWith(item.link));
              return (
                <Link
                  key={index}
                  to={item.link}
                  className={`block text-base font-semibold py-3 px-4 rounded-lg transition-colors text-center ${
                    isActive
                      ? "text-[#546DC5] bg-blue-50/50"
                      : "text-gray-800 hover:text-[#546DC5] hover:bg-gray-50"
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {item.name}
                </Link>
              );
            })}
            
            <div className="flex flex-col items-center gap-4 mt-4 pt-4 border-t border-gray-100">
              <Link
                to="/buy-a-ticket"
                onClick={() => setIsOpen(false)}
                className="block w-full max-w-xs text-center py-3 font-bold rounded-full text-white bg-[#546DC5] hover:bg-[#4357a3] shadow-md transition-all"
              >
                Buy A Ticket
              </Link>
              <div id="google_translate_element" className="font-sm mt-2"></div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
